import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { analyticsAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';

interface Transaction {
  id: number;
  visitor_id: string;
  event_name: string;
  event_category: string;
  event_action: string;
  event_label: string;
  event_value: number;
  custom_data: any;
  tags: string[];
  created_at: string;
  visitor_info: {
    country: string;
    city: string;
    device_type: string;
    browser: string;
  };
}

const TransactionsPage: React.FC = () => {
  useDocumentTitle('Transactions');
  const { currentAsset } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7d');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  useEffect(() => {
    if (currentAsset) {
      fetchTransactions();
    }
  }, [currentAsset, period]);

  const fetchTransactions = async () => {
    if (!currentAsset) return;

    setLoading(true);
    try {
      const response = await analyticsAPI.getEvents(currentAsset.id, period);
      if (response.data.status === 'success') {
        setTransactions(response.data.data.events || []);
      }
    } catch (error: any) {
      if (error.response?.status !== 401) {
        console.error('Failed to fetch events:', error);
        toast.error('Failed to load events data');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const filteredTransactions = transactions.filter(txn => {
    const matchesSearch =
      txn.event_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      txn.event_label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      txn.visitor_id.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesTag = !filterTag || txn.tags.includes(filterTag);
    const matchesCategory = !filterCategory || txn.event_category === filterCategory;

    return matchesSearch && matchesTag && matchesCategory;
  });

  const uniqueTags = Array.from(new Set(transactions.flatMap(t => t.tags)));
  const uniqueCategories = Array.from(new Set(transactions.map(t => t.event_category)));

  const totalRevenue = filteredTransactions.reduce((sum, txn) => sum + (txn.event_value || 0), 0);
  const avgTransactionValue = filteredTransactions.length > 0 ? totalRevenue / filteredTransactions.length : 0;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted">Loading events...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container-fluid">
        {/* Page Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div>
                <h2 className="h3 mb-1">Events</h2>
                <p className="text-muted mb-0">Track custom events and conversions from your website</p>
              </div>
              <div className="btn-group">
                <button
                  className={`btn btn-sm ${period === '24h' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setPeriod('24h')}
                >
                  24h
                </button>
                <button
                  className={`btn btn-sm ${period === '7d' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setPeriod('7d')}
                >
                  7d
                </button>
                <button
                  className={`btn btn-sm ${period === '30d' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setPeriod('30d')}
                >
                  30d
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="row g-4 mb-4">
          <div className="col-12 col-md-6 col-xl-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="p-3 bg-primary bg-opacity-10 text-primary rounded me-3">
                    <i className="bi bi-receipt fs-3"></i>
                  </div>
                  <div>
                    <p className="text-muted small mb-1">Total Transactions</p>
                    <h3 className="h4 mb-0">{filteredTransactions.length}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="p-3 bg-success bg-opacity-10 text-success rounded me-3">
                    <i className="bi bi-currency-dollar fs-3"></i>
                  </div>
                  <div>
                    <p className="text-muted small mb-1">Total Revenue</p>
                    <h3 className="h4 mb-0">{formatCurrency(totalRevenue)}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="p-3 bg-warning bg-opacity-10 text-warning rounded me-3">
                    <i className="bi bi-graph-up fs-3"></i>
                  </div>
                  <div>
                    <p className="text-muted small mb-1">Avg. Transaction Value</p>
                    <h3 className="h5 mb-0">{formatCurrency(avgTransactionValue)}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="p-3 bg-info bg-opacity-10 text-info rounded me-3">
                    <i className="bi bi-tags fs-3"></i>
                  </div>
                  <div>
                    <p className="text-muted small mb-1">Unique Visitors</p>
                    <h3 className="h4 mb-0">{new Set(filteredTransactions.map(t => t.visitor_id)).size}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <div className="input-group">
                      <span className="input-group-text bg-white">
                        <i className="bi bi-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by event name, label, or visitor ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-3">
                    <select
                      className="form-select"
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                    >
                      <option value="">All Categories</option>
                      {uniqueCategories.map((cat, index) => (
                        <option key={index} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-3">
                    <select
                      className="form-select"
                      value={filterTag}
                      onChange={(e) => setFilterTag(e.target.value)}
                    >
                      <option value="">All Tags</option>
                      {uniqueTags.map((tag, index) => (
                        <option key={index} value={tag}>{tag}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Integration Instructions */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-info">
              <div className="d-flex">
                <div className="me-3">
                  <i className="bi bi-info-circle fs-4"></i>
                </div>
                <div>
                  <h6 className="alert-heading">How to Track Transactions</h6>
                  <p className="mb-2">Use the CloudAyn SDK to track custom transactions on your website:</p>
                  <pre className="bg-dark text-light p-2 rounded mb-0"><code>{`CloudAyn.trackEvent('purchase_completed', {
  category: 'ecommerce',
  action: 'purchase',
  label: 'Product Name',
  value: 99.99,
  data: {
    product_id: '123',
    quantity: 2
  }
});`}</code></pre>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">All Transactions</h5>
                <button className="btn btn-sm btn-outline-primary">
                  <i className="bi bi-download me-2"></i>Export CSV
                </button>
              </div>
              <div className="card-body p-0">
                {filteredTransactions.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Event</th>
                          <th>Category</th>
                          <th>Value</th>
                          <th>Tags</th>
                          <th>Visitor Info</th>
                          <th>Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTransactions.map((txn) => (
                          <tr key={txn.id}>
                            <td>
                              <div>
                                <div className="fw-semibold">{txn.event_name}</div>
                                <small className="text-muted">{txn.event_label}</small>
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-secondary">{txn.event_category}</span>
                            </td>
                            <td className="fw-semibold">{formatCurrency(txn.event_value)}</td>
                            <td>
                              {txn.tags.map((tag, index) => (
                                <span key={index} className="badge bg-primary me-1">{tag}</span>
                              ))}
                            </td>
                            <td>
                              <div className="small">
                                <div><i className="bi bi-geo-alt me-1"></i>{txn.visitor_info.city}, {txn.visitor_info.country}</div>
                                <div><i className="bi bi-phone me-1"></i>{txn.visitor_info.device_type}</div>
                              </div>
                            </td>
                            <td className="small">{formatDate(txn.created_at)}</td>
                            <td>
                              <button className="btn btn-sm btn-outline-primary">
                                <i className="bi bi-eye"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center text-muted py-5">
                    <i className="bi bi-receipt fs-1 d-block mb-3"></i>
                    <p className="mb-2">No transactions recorded yet</p>
                    <small>Start tracking custom events from your website using the CloudAyn SDK</small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TransactionsPage;
